import { useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook for keyboard navigation
 * Provides focus management, tab order, and keyboard shortcuts
 */
export const useKeyboardNavigation = (options = {}) => {
  const {
    enableArrowKeys = true,
    enableTabNavigation = true,
    enableEscapeKey = true,
    enableEnterKey = true,
    onEscape = null,
    onEnter = null,
    focusableSelector = 'button, [tabindex], input, select, textarea, a[href]'
  } = options;

  const containerRef = useRef(null);
  const focusableElementsRef = useRef([]);
  const currentFocusIndexRef = useRef(-1);

  // Get all focusable elements within the container
  const getFocusableElements = useCallback(() => {
    if (!containerRef.current) return [];
    
    const elements = Array.from(
      containerRef.current.querySelectorAll(focusableSelector)
    ).filter(element => {
      // Filter out disabled and hidden elements
      return !element.disabled && 
             !element.hidden && 
             element.offsetParent !== null &&
             element.getAttribute('tabindex') !== '-1';
    });
    
    focusableElementsRef.current = elements;
    return elements;
  }, [focusableSelector]);

  // Focus a specific element by index
  const focusElement = useCallback((index) => {
    const elements = getFocusableElements();
    if (elements.length === 0) return;
    
    const targetIndex = Math.max(0, Math.min(index, elements.length - 1));
    const element = elements[targetIndex];
    
    if (element) {
      element.focus();
      currentFocusIndexRef.current = targetIndex;
    }
  }, [getFocusableElements]);

  // Focus next element
  const focusNext = useCallback(() => {
    const elements = getFocusableElements();
    if (elements.length === 0) return;
    
    const nextIndex = (currentFocusIndexRef.current + 1) % elements.length;
    focusElement(nextIndex);
  }, [getFocusableElements, focusElement]);

  // Focus previous element
  const focusPrevious = useCallback(() => {
    const elements = getFocusableElements();
    if (elements.length === 0) return;
    
    const prevIndex = currentFocusIndexRef.current <= 0 
      ? elements.length - 1 
      : currentFocusIndexRef.current - 1;
    focusElement(prevIndex);
  }, [getFocusableElements, focusElement]);

  // Focus first element
  const focusFirst = useCallback(() => {
    focusElement(0);
  }, [focusElement]);

  // Focus last element
  const focusLast = useCallback(() => {
    const elements = getFocusableElements();
    focusElement(elements.length - 1);
  }, [getFocusableElements, focusElement]);

  // Handle keyboard events
  const handleKeyDown = useCallback((event) => {
    const { key, ctrlKey, altKey, shiftKey, target } = event;
    
    // Skip if modifier keys are pressed (except Shift for Tab)
    if (ctrlKey || altKey) return;
    
    // CRITICAL: Allow arrow keys to work normally in text inputs and textareas
    // This allows users to move the cursor within the text field
    const isTextInput = target && (
      target.tagName === 'INPUT' || 
      target.tagName === 'TEXTAREA' ||
      target.isContentEditable ||
      target.contentEditable === 'true'
    );
    
    // If user is typing in a text field, allow arrow keys for cursor movement
    if (isTextInput && (key === 'ArrowUp' || key === 'ArrowDown' || key === 'ArrowLeft' || key === 'ArrowRight')) {
      // Don't prevent default - let the browser handle cursor movement
      return;
    }
    
    switch (key) {
      case 'Tab':
        if (enableTabNavigation) {
          // Let browser handle default tab behavior
          // Just update our focus index
          setTimeout(() => {
            const elements = getFocusableElements();
            const activeElement = document.activeElement;
            const index = elements.indexOf(activeElement);
            if (index !== -1) {
              currentFocusIndexRef.current = index;
            }
          }, 0);
        }
        break;
        
      case 'ArrowDown':
      case 'ArrowRight':
        if (enableArrowKeys) {
          event.preventDefault();
          focusNext();
        }
        break;
        
      case 'ArrowUp':
      case 'ArrowLeft':
        if (enableArrowKeys) {
          event.preventDefault();
          focusPrevious();
        }
        break;
        
      case 'Home':
        if (enableArrowKeys) {
          event.preventDefault();
          focusFirst();
        }
        break;
        
      case 'End':
        if (enableArrowKeys) {
          event.preventDefault();
          focusLast();
        }
        break;
        
      case 'Escape':
        if (enableEscapeKey) {
          event.preventDefault();
          onEscape && onEscape();
        }
        break;
        
      case 'Enter':
      case ' ':
        if (enableEnterKey) {
          const activeElement = document.activeElement;
          if (activeElement && activeElement.click) {
            // Let the element handle the click
            // onEnter callback can be used for additional logic
            onEnter && onEnter(activeElement);
          }
        }
        break;
    }
  }, [
    enableTabNavigation,
    enableArrowKeys,
    enableEscapeKey,
    enableEnterKey,
    onEscape,
    onEnter,
    focusNext,
    focusPrevious,
    focusFirst,
    focusLast,
    getFocusableElements
  ]);

  // Set up event listeners
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('keydown', handleKeyDown);
    
    // Initialize focusable elements
    getFocusableElements();
    
    return () => {
      container.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown, getFocusableElements]);

  return {
    containerRef,
    focusNext,
    focusPrevious,
    focusFirst,
    focusLast,
    focusElement,
    getFocusableElements
  };
};

/**
 * Hook for managing focus within a specific component
 */
export const useFocusManagement = () => {
  const focusStackRef = useRef([]);
  
  const pushFocus = useCallback((element) => {
    if (element) {
      focusStackRef.current.push(document.activeElement);
      element.focus();
    }
  }, []);
  
  const popFocus = useCallback(() => {
    const previousElement = focusStackRef.current.pop();
    if (previousElement) {
      previousElement.focus();
    }
  }, []);
  
  const clearFocusStack = useCallback(() => {
    focusStackRef.current = [];
  }, []);
  
  return {
    pushFocus,
    popFocus,
    clearFocusStack
  };
};

export default useKeyboardNavigation;
